import React, { useEffect, useState } from "react";

import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "lucide-react";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  eachHourOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getHours,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";

import { capitalize, cn } from "@/lib/utils";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color?: "sky" | "amber" | "violet" | "rose" | "emerald" | "orange";
  allDay?: boolean;
  description?: string;
}

export interface ExtendedCalendarItem extends CalendarEvent {
  original: any;
}

// Define the calendar view type
type CalendarView = "month" | "week" | "day" | "agenda";
const views = ["Month", "Week", "Day", "Agenda"];

function getEventsForDay(events: CalendarEvent[], day: Date): CalendarEvent[] {
  return events.filter((event) => isSameDay(day, new Date(event.start)));
}

function getEventsForWeek(
  events: CalendarEvent[],
  startDate: Date,
  endDate: Date
): CalendarEvent[] {
  return events.filter((event) => {
    const eventDate = new Date(event.start);
    return eventDate >= startDate && eventDate <= endDate;
  });
}

function getEventColorClasses(color?: string): string {
  switch (color) {
    case "sky":
      return "aero:bg-sky-100 aero:text-sky-700 aero:dark:bg-sky-900/30 aero:dark:text-sky-300";
    case "amber":
      return "aero:bg-amber-100 aero:text-amber-700 aero:dark:bg-amber-900/30 aero:dark:text-amber-300";
    case "violet":
      return "aero:bg-violet-100 aero:text-violet-700 aero:dark:bg-violet-900/30 aero:dark:text-violet-300";
    case "rose":
      return "aero:bg-rose-100 aero:text-rose-700 aero:dark:bg-rose-900/30 aero:dark:text-rose-300";
    case "emerald":
      return "aero:bg-emerald-100 aero:text-emerald-700 aero:dark:bg-emerald-900/30 aero:dark:text-emerald-300";
    case "orange":
      return "aero:bg-orange-100 aero:text-orange-700 aero:dark:bg-orange-900/30 aero:dark:text-orange-300";
    default:
      return "aero:bg-sky-100 aero:text-sky-700 aero:dark:bg-sky-900/30 aero:dark:text-sky-300";
  }
}

export function SimpleCalendar({
  calendarEvents = [],
}: {
  calendarEvents: ExtendedCalendarItem[];
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [view, setView] = useState<CalendarView>("month");
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDescription, setNewEventDescription] = useState("");

  useEffect(() => {
    if (calendarEvents.length > 0) {
      setEvents(calendarEvents);
    }
  }, [calendarEvents]);

  // Navigation handlers
  const handlePrevious = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(subWeeks(currentDate, 1));
    } else if (view === "day") {
      setCurrentDate(subDays(currentDate, 1));
    } else if (view === "agenda") {
      setCurrentDate(subDays(currentDate, 7));
    }
  };

  const handleNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else if (view === "agenda") {
      setCurrentDate(addDays(currentDate, 7));
    }
  };

  const handleToday = () => setCurrentDate(new Date());

  // View selection handlers
  const handleViewChange = (newView: CalendarView) => {
    setView(newView);
  };

  // Add new event
  const handleAddEvent = () => {
    if (newEventTitle.trim() === "") return;

    const newEvent: CalendarEvent = {
      id: Math.random().toString(36).substring(2, 11),
      title: newEventTitle,
      description: newEventDescription,
      start: currentDate,
      end: currentDate,
      color: "sky",
    };

    setEvents([...events, newEvent]);
    setNewEventTitle("");
    setNewEventDescription("");
    setIsEventDialogOpen(false);
  };

  // Month View
  const renderMonthView = () => {
    // Calculate days to display in the calendar
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 }),
      end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 }),
    });

    // Generate weekday headers
    const weekdays = Array.from({ length: 7 }).map((_, i) => {
      const date = addDays(startOfWeek(new Date()), i);
      return format(date, "EEE");
    });

    // Group days into weeks
    const weeks = [];
    let week = [];

    for (let i = 0; i < days.length; i++) {
      week.push(days[i]);
      if (week.length === 7 || i === days.length - 1) {
        weeks.push(week);
        week = [];
      }
    }

    return (
      <>
        <div className="aero:grid aero:grid-cols-7 aero:border-b">
          {weekdays.map((day) => (
            <div
              key={day}
              className="aero:py-2 aero:text-center aero:text-sm aero:text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="aero:grid aero:flex-1 aero:grid-cols-7">
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={`week-${weekIndex}`}>
              {week.map((day) => {
                const dayEvents = getEventsForDay(events, day);
                const isCurrentMonth = isSameMonth(day, currentDate);

                return (
                  <div
                    key={day.toString()}
                    className={cn(
                      "aero:min-h-[100px] aero:border-r aero:border-b aero:p-1",
                      !isCurrentMonth &&
                        "aero:bg-muted/50 aero:text-muted-foreground",
                      isToday(day) && "aero:bg-muted/80"
                    )}
                  >
                    <div className="aero:flex aero:justify-between">
                      <span
                        className={cn(
                          "aero:inline-flex aero:h-6 aero:w-6 aero:items-center aero:justify-center aero:!rounded-full aero:text-sm",
                          isToday(day) &&
                            "aero:bg-brand-primary aero:text-brand-primary-foreground aero:font-medium"
                        )}
                      >
                        {format(day, "d")}
                      </span>
                    </div>
                    <div className="aero:mt-1 aero:space-y-1">
                      {dayEvents.map((event) => (
                        <div
                          key={event.id}
                          className={cn(
                            "aero:text-xs aero:px-1 aero:py-0.5 aero:!rounded aero:truncate",
                            getEventColorClasses(event.color)
                          )}
                        >
                          {event.title}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </>
    );
  };

  // Week View
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    const hours = eachHourOfInterval({
      start: startOfDay(currentDate),
      end: endOfDay(currentDate),
    });

    return (
      <div className="aero:flex aero:flex-col aero:h-full">
        <div className="aero:grid aero:grid-cols-8 aero:border-b">
          <div className="aero:py-2 aero:text-center aero:text-sm aero:text-muted-foreground">
            Time
          </div>
          {days.map((day) => (
            <div
              key={day.toString()}
              className={cn(
                "aero:py-2 aero:text-center aero:text-sm",
                isToday(day) ? "aero:font-medium" : "aero:text-muted-foreground"
              )}
            >
              {format(day, "EEE dd")}
            </div>
          ))}
        </div>

        <div className="aero:grid aero:grid-cols-8 aero:flex-1 aero:overflow-auto">
          <div className="aero:border-r">
            {hours.map((hour) => (
              <div
                key={hour.toString()}
                className="aero:h-12 aero:border-b aero:px-2 aero:text-xs aero:text-muted-foreground"
              >
                {format(hour, "h a")}
              </div>
            ))}
          </div>

          {days.map((day) => {
            const dayEvents = getEventsForDay(events, day);

            return (
              <div key={day.toString()} className="aero:border-r">
                {hours.map((hour) => {
                  const hourEvents = dayEvents.filter(
                    (event) =>
                      getHours(new Date(event.start)) === getHours(hour)
                  );

                  return (
                    <div
                      key={hour.toString()}
                      className="aero:h-12 aero:border-b aero:p-1"
                    >
                      {hourEvents.map((event) => (
                        <div
                          key={event.id}
                          className={cn(
                            "aero:text-xs aero:px-1 aero:py-0.5 aero:!rounded aero:truncate",
                            getEventColorClasses(event.color)
                          )}
                        >
                          {event.title}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Day View
  const renderDayView = () => {
    const hours = eachHourOfInterval({
      start: startOfDay(currentDate),
      end: endOfDay(currentDate),
    });
    const dayEvents = getEventsForDay(events, currentDate);

    return (
      <div className="aero:flex aero:flex-col aero:h-full">
        <div className="aero:text-center aero:py-2 aero:border-b aero:font-medium">
          {format(currentDate, "EEEE, MMMM d, yyyy")}
        </div>

        <div className="aero:grid aero:grid-cols-[80px_1fr] aero:flex-1 aero:overflow-auto">
          <div className="aero:border-r">
            {hours.map((hour) => (
              <div
                key={hour.toString()}
                className="aero:h-16 aero:border-b aero:px-2 aero:text-xs aero:text-muted-foreground"
              >
                {format(hour, "h a")}
              </div>
            ))}
          </div>

          <div>
            {hours.map((hour) => {
              const hourEvents = dayEvents.filter(
                (event) => getHours(new Date(event.start)) === getHours(hour)
              );

              return (
                <div
                  key={hour.toString()}
                  className="aero:h-16 aero:border-b aero:p-1"
                >
                  {hourEvents.map((event) => (
                    <div
                      key={event.id}
                      className={cn(
                        "aero:text-xs aero:px-2 aero:py-1 aero:!rounded aero:truncate aero:mb-1",
                        getEventColorClasses(event.color)
                      )}
                    >
                      {event.title}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Agenda View
  const renderAgendaView = () => {
    // Show events for the next 14 days
    const startDate = startOfDay(currentDate);
    const endDate = addDays(startDate, 14);
    const agendaEvents = getEventsForWeek(events, startDate, endDate);

    // Group events by date
    const eventsByDate: Record<string, CalendarEvent[]> = {};

    agendaEvents.forEach((event) => {
      const dateKey = format(new Date(event.start), "yyyy-MM-dd");
      if (!eventsByDate[dateKey]) {
        eventsByDate[dateKey] = [];
      }
      eventsByDate[dateKey].push(event);
    });

    return (
      <div className="aero:flex aero:flex-col aero:h-full aero:overflow-auto aero:p-4">
        <h2 className="aero:text-lg aero:font-medium aero:mb-4">
          Upcoming Events: {format(startDate, "MMM d")} -{" "}
          {format(endDate, "MMM d, yyyy")}
        </h2>

        {Object.keys(eventsByDate).length === 0 ? (
          <div className="aero:text-center aero:text-muted-foreground aero:py-8">
            No events scheduled
          </div>
        ) : (
          Object.keys(eventsByDate)
            .sort()
            .map((dateKey) => {
              const date = new Date(dateKey);
              return (
                <div key={dateKey} className="aero:mb-6">
                  <h3
                    className={cn(
                      "aero:text-sm aero:font-medium aero:mb-2 aero:pb-1 aero:border-b",
                      isToday(date) && "text-brand-primary"
                    )}
                  >
                    {format(date, "EEEE, MMMM d")} {isToday(date) && "(Today)"}
                  </h3>
                  <div className="aero:space-y-2">
                    {eventsByDate[dateKey].map((event) => (
                      <div
                        key={event.id}
                        className={cn(
                          "aero:p-2 aero:!rounded-md aero:border",
                          getEventColorClasses(event.color)
                        )}
                      >
                        <div className="aero:font-medium">{event.title}</div>
                        <div className="aero:text-xs aero:mt-1">
                          {format(new Date(event.start), "h:mm a")}
                          {event.description && (
                            <p className="aero:mt-1 aero:text-xs aero:opacity-80">
                              {event.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
        )}
      </div>
    );
  };

  // Render the current view
  const renderCurrentView = () => {
    switch (view) {
      case "month":
        return renderMonthView();
      case "week":
        return renderWeekView();
      case "day":
        return renderDayView();
      case "agenda":
        return renderAgendaView();
      default:
        return renderMonthView();
    }
  };

  // Format the title based on the current view
  const getViewTitle = () => {
    switch (view) {
      case "month":
        return format(currentDate, "MMMM yyyy");
      case "week": {
        const start = startOfWeek(currentDate, { weekStartsOn: 0 });
        const end = endOfWeek(currentDate, { weekStartsOn: 0 });
        return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
      }
      case "day":
        return format(currentDate, "MMMM d, yyyy");
      case "agenda":
        return "Agenda View";
      default:
        return format(currentDate, "MMMM yyyy");
    }
  };

  return (
    <div className="aero:flex aero:flex-col aero:!rounded-lg aero:border aero:h-full">
      <div className="aero:flex aero:items-center aero:justify-between aero:p-4">
        <div className="aero:flex aero:items-center aero:gap-4">
          <Button intent="primary" design="outline" onClick={handleToday}>
            Today
          </Button>
          <div className="aero:flex aero:items-center aero:gap-2">
            <Button
              design="text"
              size="iconMd"
              onClick={handlePrevious}
              aria-label="Previous"
            >
              <ChevronLeftIcon size={16} />
            </Button>
            <Button
              design="text"
              size="iconMd"
              onClick={handleNext}
              aria-label="Next"
            >
              <ChevronRightIcon size={16} />
            </Button>
          </div>
          <h2 className="aero:text-lg aero:font-semibold">{getViewTitle()}</h2>
        </div>

        <div className="aero:flex aero:items-center aero:gap-2">
          <Select
            value={capitalize(view)}
            onValueChange={(newView) =>
              handleViewChange(newView.toLowerCase() as CalendarView)
            }
          >
            <SelectTrigger className="aero:w-[180px]">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              {views.map((name: string) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={() => setIsEventDialogOpen(true)} disabled>
            <PlusIcon className="aero:h-4 aero:w-4 aero:mr-1" />
            New Event
          </Button>
        </div>
      </div>

      <div className="aero:flex-1 aero:overflow-auto">
        {renderCurrentView()}
      </div>

      <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Event</DialogTitle>
          </DialogHeader>
          <div className="aero:grid aero:gap-4 aero:py-4">
            <div className="aero:grid aero:gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="Event title"
              />
            </div>
            <div className="aero:grid aero:gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={newEventDescription}
                onChange={(e) => setNewEventDescription(e.target.value)}
                placeholder="Event description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              intent="primary"
              design="outline"
              onClick={() => setIsEventDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAddEvent}>Add Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
